import React, { FC, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { RecommendationDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import { StyledChip } from "./Actions.styled";

import {
  useRecommendationsApi,
  useSetRecommendationComplete,
  useSetRecommendationNotRelevant,
} from "@api";
import { ConfirmationModal } from "@components/common";
import { Button, Stack, IconButton } from "@components/base";
import { useUserStore } from "@store/user";

interface RecomendationDetailsActions {
  recommendation: RecommendationDto;
}

const RecomendationDetailsActions: FC<RecomendationDetailsActions> = ({
  recommendation,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    setRecommendationComplete,
    setRecommendationCompleteEnabled,
    isPendingRecommendationComplete,
  } = useSetRecommendationComplete(user);
  const {
    setRecommendationNotRelevant,
    setRecommendationNotRelevantEnabled,
    isPendingRecommendationNotRelevant,
  } = useSetRecommendationNotRelevant(user);
  const { isFetchingRecommendations } = useRecommendationsApi(user);
  const [confirmCompleted, setConfirmCompleted] = useState(false);
  const [confirmNotRelevant, setConfirmNotRelevant] = useState(false);

  const handleBack = (): void => {
    history.back();
  };

  const handleComplete = (): void => {
    setConfirmCompleted(true);
  };

  const handleNotRelevant = (): void => {
    setConfirmNotRelevant(true);
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Stack spacing={1} direction="row" alignItems="center">
          <IconButton size="small" onClick={handleBack}>
            <FeatherIcon icon="arrow-left" size={16} />
          </IconButton>
          {recommendation.scope?.includes("1") && (
            <StyledChip label="Scope 1" size="small" scope={1} />
          )}
          {recommendation.scope?.includes("2") && (
            <StyledChip label="Scope 2" size="small" scope={2} />
          )}
          {recommendation.scope?.includes("3") && (
            <StyledChip label="Scope 3" size="small" scope={3} />
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FeatherIcon icon="book-open" size={12} />}
          >
            {t("recommendationActions.learnMore")}
          </Button>
          {setRecommendationCompleteEnabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FeatherIcon icon="check-circle" size={12} />}
              onClick={handleComplete}
              disabled={
                isPendingRecommendationComplete ||
                recommendation.isCompleted ||
                recommendation.isNotRelevant ||
                isFetchingRecommendations
              }
            >
              {t("recommendationActions.completed")}
            </Button>
          )}
          {setRecommendationNotRelevantEnabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FeatherIcon icon="eye-off" size={12} />}
              onClick={handleNotRelevant}
              disabled={
                isPendingRecommendationNotRelevant ||
                recommendation.isCompleted ||
                recommendation.isNotRelevant ||
                isFetchingRecommendations
              }
            >
              {t("recommendationActions.notRelevant")}
            </Button>
          )}
        </Stack>
      </Stack>
      <ConfirmationModal
        open={confirmCompleted}
        onClose={() => setConfirmCompleted(false)}
        onConfirm={() => {
          setConfirmCompleted(false);
          setRecommendationComplete(parseInt(recommendation.external_id));
        }}
        title={t("recommendationActions.confirmTitle")}
        description={t("recommendationActions.confirmDescription")}
      />
      <ConfirmationModal
        open={confirmNotRelevant}
        onClose={() => setConfirmNotRelevant(false)}
        onConfirm={() => {
          setConfirmNotRelevant(false);
          setRecommendationNotRelevant(parseInt(recommendation.external_id));
        }}
        title={t("recommendationActions.confirmTitle")}
        description={t("recommendationActions.confirmDescription")}
      />
    </>
  );
};

export default RecomendationDetailsActions;
